import {
  ApplicantStatus,
  ExecutionModes,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { AlertCircle, Loading02, Minus } from "untitledui-js-base";
import { useQuery } from "@tanstack/react-query";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import api from "api/landlord/applications";
import { errorHitCodes } from "views/applications-id-report/slices/CreditReportSlice/utils";
import { cx } from "utils/classnames";

interface StatusIconProps {
  applicantStatus?: ApplicantStatus;
  verificationStatus: VerificationStatus;
  name: VerificationNames;
  executionMode?: ExecutionModes;
  isManuallyOverridden: boolean;
  isIdentityVerificationOverrideRequired: boolean;
  applicationId: number;
  applicantId: number;
  verificationId: number;
  isSelected: boolean;
  isRerun: boolean;
  isAwaitingForCounty?: boolean;
  isSsnMismatched?: boolean;
  hasWarnings?: boolean;
}

export const StatusIcon = ({
  applicantStatus,
  verificationStatus,
  name,
  executionMode,
  isManuallyOverridden,
  isIdentityVerificationOverrideRequired,
  applicationId,
  applicantId,
  verificationId,
  isSelected,
  isRerun,
  isAwaitingForCounty,
  isSsnMismatched,
  hasWarnings = false
}: StatusIconProps) => {
  const { data: creditReport } = useQuery<VerificationReport<CreditReport>>({
    queryKey: ["CREDIT_REPORT", applicationId, verificationId, applicantId],
    queryFn: () =>
      api
        .getApplicantVerification(applicationId, applicantId, verificationId)
        .then((res) => res.verification),
    enabled: name === VerificationNames.CreditReport
  });

  if (isAwaitingForCounty)
    return <Loading02 className="w-4 h-4 stroke-warning-500" />;

  if (isManuallyOverridden) return;

  if (
    isIdentityVerificationOverrideRequired &&
    [
      VerificationNames.CreditReport,
      VerificationNames.CriminalReport,
      VerificationNames.EvictionReport
    ].includes(name)
  )
    return <Minus className="stroke-content-100 w-4 h-4" />;

  if (
    (isRerun || applicantStatus === ApplicantStatus.Submitted) &&
    verificationStatus === VerificationStatus.Pending
  )
    return (
      <Loading02
        className={cx(
          "stroke-content-100 w-4 h-4",
          isSelected ? "stroke-primary-500" : "stroke-content-100"
        )}
      />
    );

  if (
    (verificationStatus === VerificationStatus.Pending &&
      executionMode === ExecutionModes.Manual &&
      applicantStatus === ApplicantStatus.Submitted) ||
    errorHitCodes.includes(creditReport?.report.hit_code?.code || "")
  )
    return <AlertCircle className="stroke-danger-500" size="16px" />;

  if (name === VerificationNames.IdentityVerification)
    switch (verificationStatus) {
      case VerificationStatus.FinishedWithWarnings:
        return <AlertCircle className="stroke-warning-600" size="16px" />;
      case VerificationStatus.Failed:
        return <AlertCircle className="stroke-danger-500" size="16px" />;
    }

  if (hasWarnings || isSsnMismatched)
    return <AlertCircle className="stroke-warning-600 w-4 h-4" />;

  switch (verificationStatus) {
    // When Credit report, Criminal report, and Housing court report are unable to render,
    // or Income history doesn’t have any verified income
    case VerificationStatus.Failed:
      return <AlertCircle className="stroke-danger-500 w-4 h-4" />;
    // When Income history (fraudulent documents, invalid documents), Credit report (applicant doesn’t have credit data),
    // and Criminal report (received warning from CIC) are completed with warnings
    case VerificationStatus.FinishedWithWarnings:
      return <AlertCircle className="stroke-warning-600 w-4 h-4" />;
    // When Rental application, Income history, IDV, Housing history, Credit report,
    // Criminal report, and Housing court report are in progress
    case VerificationStatus.Processing:
    case VerificationStatus.Started:
      return (
        <Loading02
          className={cx(
            "stroke-content-100 w-4 h-4",
            isSelected ? "stroke-primary-500" : "stroke-content-100"
          )}
        />
      );
    // When Rental application, Income history, IDV, Housing history, Credit report, Criminal report,
    // and Housing court report are not started
    case VerificationStatus.Pending:
    case VerificationStatus.Skipped:
      return <Minus className="stroke-content-100 w-4 h-4" />;
  }
};
